import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  applyPresetToState,
  loadPrecisionState,
  persistPrecisionState,
  PrecisionPreset,
  PrecisionSettings,
  PrecisionState,
  normalizePrecisionSettings,
} from '../lib/precisionSettings';

interface UsePrecisionSettingsResult {
  state: PrecisionState | null;
  loading: boolean;
  error: Error | null;
  updateSettings: (updates: Partial<PrecisionSettings>) => void;
  applyPreset: (presetId: string) => void;
  createPreset: (name: string) => void;
  renamePreset: (presetId: string, name: string) => void;
  deletePreset: (presetId: string) => void;
}

const generatePresetId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `preset-${crypto.randomUUID()}`;
  }
  return `preset-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const sanitizeName = (name: string) => {
  const trimmed = name.trim();
  return trimmed.length > 0 ? trimmed : 'Untitled preset';
};

export function usePrecisionSettings(): UsePrecisionSettingsResult {
  const [state, setState] = useState<PrecisionState | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const initializedRef = useRef(false);
  const persistPromiseRef = useRef<Promise<void> | null>(null);

  useEffect(() => {
    let cancelled = false;
    loadPrecisionState()
      .then((loaded) => {
        if (cancelled) {
          return;
        }
        setState(loaded);
      })
      .catch((err) => {
        if (cancelled) {
          return;
        }
        console.error('Failed to load precision settings:', err);
        setError(err instanceof Error ? err : new Error(String(err)));
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
          initializedRef.current = true;
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const schedulePersist = useCallback((nextState: PrecisionState) => {
    if (!initializedRef.current) {
      return;
    }
    persistPromiseRef.current = persistPrecisionState(nextState).catch((err) => {
      console.error('Failed to persist precision settings:', err);
    });
  }, []);

  const updateSettings = useCallback((updates: Partial<PrecisionSettings>) => {
    setState((previous) => {
      if (!previous) {
        return previous;
      }
      const merged = {
        ...previous,
        settings: normalizePrecisionSettings({
          ...previous.settings,
          ...updates,
        }),
      };
      schedulePersist(merged);
      return merged;
    });
  }, [schedulePersist]);

  const applyPreset = useCallback(
    (presetId: string) => {
      setState((previous) => {
        if (!previous) {
          return previous;
        }
        const preset = previous.presets.find((item) => item.id === presetId);
        if (!preset) {
          return previous;
        }
        const updated = applyPresetToState(previous, preset);
        schedulePersist(updated);
        return updated;
      });
    },
    [schedulePersist],
  );

  const createPreset = useCallback(
    (name: string) => {
      setState((previous) => {
        if (!previous) {
          return previous;
        }
        const preset: PrecisionPreset = {
          id: generatePresetId(),
          name: sanitizeName(name),
          settings: normalizePrecisionSettings(previous.settings),
          isBuiltin: false,
        };
        const nextState: PrecisionState = {
          settings: normalizePrecisionSettings(previous.settings),
          presets: [...previous.presets.filter((item) => item.id !== preset.id), preset],
          activePresetId: preset.id,
        };
        schedulePersist(nextState);
        return nextState;
      });
    },
    [schedulePersist],
  );

  const renamePreset = useCallback(
    (presetId: string, name: string) => {
      setState((previous) => {
        if (!previous) {
          return previous;
        }
        const normalizedName = sanitizeName(name);
        const presets = previous.presets.map((preset) =>
          preset.id === presetId && !preset.isBuiltin
            ? { ...preset, name: normalizedName }
            : preset,
        );
        const nextState: PrecisionState = {
          settings: previous.settings,
          presets,
          activePresetId: previous.activePresetId,
        };
        schedulePersist(nextState);
        return nextState;
      });
    },
    [schedulePersist],
  );

  const deletePreset = useCallback(
    (presetId: string) => {
      setState((previous) => {
        if (!previous) {
          return previous;
        }
        const target = previous.presets.find((preset) => preset.id === presetId);
        if (!target || target.isBuiltin) {
          return previous;
        }
        const presets = previous.presets.filter((preset) => preset.id !== presetId);
        const activePresetId = previous.activePresetId === presetId ? null : previous.activePresetId;
        const nextState: PrecisionState = {
          settings: normalizePrecisionSettings(previous.settings),
          presets,
          activePresetId,
        };
        schedulePersist(nextState);
        return nextState;
      });
    },
    [schedulePersist],
  );

  useEffect(() => {
    return () => {
      if (persistPromiseRef.current) {
        void persistPromiseRef.current.then(() => undefined);
      }
    };
  }, []);

  return useMemo(
    () => ({
      state,
      loading,
      error,
      updateSettings,
      applyPreset,
      createPreset,
      renamePreset,
      deletePreset,
    }),
    [state, loading, error, updateSettings, applyPreset, createPreset, renamePreset, deletePreset],
  );
}
